"use client"

import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { BASE_API_URL } from "../config"
import {
  QuestionsCalling,
  QuestionsStrategy,
  QuestionsSpeaker,
  QuestionsValueLadder,
  QuestionValueProposition,
  QuestionsTargetMarket,
  QuestionsTrafficSources,
  QuestionsIrresistible,
  QuestionConsultingNiche,
  QuestionCoachingNiche,
  QuestionMasterbot,
  QuestionsWorkflow,
} from "../data/questions"

import { ArrowLeft, ChevronRight, HelpCircle } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card"
import { Button } from "../components/ui/Button"
import { Textarea } from "../components/ui/Textarea"

const QuestionsPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const selectedType = location.state?.type
  const userEmail = location.state?.email

  const [questions, setQuestions] = useState([])
  const [appTitle, setAppTitle] = useState("")
  const [formData, setFormData] = useState({})
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [oldPrompt, setOldPrompt] = useState("")
  const [systemprompt, setsystemprompt] = useState("")
  const [progress, setProgress] = useState(0)
  const [costPrompt, setCostPrompt] = useState("")

  // Simulated load from MongoDB
  useEffect(() => {
    if (!selectedType || !userEmail) {
      navigate("/")
      return
    }

    const map = {
      "find-calling": { data: QuestionsCalling, title: "Find Your Calling Copilot" },
      "business-strategy": { data: QuestionsStrategy, title: "Business Strategy Copilot" },
      "find-speaker": { data: QuestionsSpeaker, title: "Find Your Speaker" },
      "value-ladder": { data: QuestionsValueLadder, title: "Value Ladder Copilot" },
      "value-proposition": { data: QuestionValueProposition, title: "Value Proposition Copilot" },
      "target-market": { data: QuestionsTargetMarket, title: "Target Market Copilot" },
      "traffic-sources": { data: QuestionsTrafficSources, title: "Traffic Sources Copilot" },
      "irresistible-copilot": { data: QuestionsIrresistible, title: "Irresistible Offer Co-pilot" },
      "findconsultingniche-copilot": { data: QuestionConsultingNiche, title: "Find your consulting niche Co-pilot" },
      "findyourcoaching-copilot": { data: QuestionCoachingNiche, title: "Find your coaching niche Co-pilot" },
      masterbot: { data: QuestionMasterbot, title: "Master prompt tester" },
      aiworkflow: { data: QuestionsWorkflow, title: "Ai Workflow" },
    }

    const selected = map[selectedType]
    if (!selected) return navigate("/")

    setQuestions(selected.data)
    setAppTitle(selected.title)

    fetch(`${BASE_API_URL}/playground/answers?email=${userEmail}&type=${selectedType}`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.responses) {
          const restored = {}
          data.responses.forEach(({ questionId, answer }) => {
            restored[questionId] = answer
          })
          setFormData(restored)
          setOldPrompt(data?.prompt)
          setsystemprompt(data?.systemprompt)
          setCostPrompt(data?.costPrompt)
        }
      })
      .catch((err) => {
        console.error("Error fetching saved data:", err)
      })
  }, [selectedType, userEmail, navigate])

  useEffect(() => {
    if (questions.length > 0) {
      const answeredQuestions = Object.keys(formData).length
      const progressPercentage = Math.round((answeredQuestions / questions.length) * 100)
      setProgress(progressPercentage)
    }
  }, [formData, questions])

  const handleInputChange = (id, value) => {
    setFormData((prev) => ({ ...prev, [id]: value }))
    setErrors((prev) => ({ ...prev, [id]: false }))
  }

  const handleSubmit = () => {
    const newErrors = {}
    questions.forEach((q) => {
      if (!formData[q.id]) {
        newErrors[q.id] = true
      }
    })

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)

      // Scroll to first error
      const firstErrorId = Object.keys(newErrors)[0]
      const errorElement = document.getElementById(`question-${firstErrorId}`)
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" })
      }

      return
    }

    const responses = questions.map((q) => ({
      questionId: q.id,
      questionText: q.question,
      answer: formData[q.id],
    }))

    const payload = {
      userEmail,
      selectedType,
      responses,
      passedSystemPrompt: systemprompt,
      costPrompt: costPrompt,
    }

    // Save locally (optional — useful for reloads)
    localStorage.setItem(`${userEmail}_${selectedType}`, JSON.stringify(payload))

    // ✅ Navigate to Prompt Page
    navigate("/prompt", {
      state: {
        userEmail,
        selectedType,
        responses,
        oldPrompt,
        systemprompt,
        costPrompt,
      },
    })
  }

  return (
    <div className="min-h-screen text-black">
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="flex items-center justify-between mb-6 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-gray-700 hover:text-black transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Home</span>
          </button>

          <div className="flex items-center gap-2">
            <div className="h-2 w-48 bg-gray-300 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <span className="text-sm text-gray-600">{progress}% complete</span>
          </div>
        </div>

        <div className="relative rounded-2xl overflow-hidden mb-8">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/80 to-blue-600/80 z-10"></div>
          <div className="relative z-20 py-12 px-6 text-center">
            <h1 className="text-white text-3xl md:text-4xl font-bold mb-2">{appTitle}</h1>
            <p className="text-gray-200 max-w-xl mx-auto">
              Answer these questions to help us understand your needs and generate personalized insights
            </p>
          </div>
        </div>

        <Card className="bg-white/95 backdrop-blur-sm border border-gray-300 rounded-xl shadow-xl mb-8">
          <CardHeader className="border-b border-gray-200 pb-4">
            <CardTitle className="text-black flex items-center justify-between">
              <span>{appTitle} - Questionnaire</span>
              <div className="text-sm font-normal text-gray-600 flex items-center gap-1">
                <HelpCircle className="h-4 w-4" />
                <span>Be detailed for better results</span>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-8">
            {questions.map((q) => (
              <div
                key={q.id}
                id={`question-${q.id}`}
                className="space-y-3 animate-fadeIn bg-white rounded-lg p-4 border border-gray-200"
              >
                <h3 className="text-black text-lg font-medium text-left">{q.question}</h3>
                <Textarea
                  placeholder="Describe your thoughts in detail..."
                  rows={q.area === "h-30" ? 3 : 4}
                  value={formData[q.id] || ""}
                  onChange={(e) => handleInputChange(q.id, e.target.value)}
                  className={`bg-white text-black border ${
                    errors[q.id]
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                  } text-black resize-none rounded-lg transition-all`}
                />
                {errors[q.id] && (
                  <div className="flex items-center gap-2 text-red-600 text-sm">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    <span>Please provide an answer to this question</span>
                  </div>
                )}
              </div>
            ))}

            <div className="flex justify-center pt-6">
              <Button
                onClick={handleSubmit}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:shadow-lg hover:shadow-purple-500/25 text-white rounded-lg px-8 py-3 flex items-center gap-2 transition-all"
              >
                Continue to Prompt
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default QuestionsPage
